/* eslint-disable no-await-in-loop */
/**
 * Affiliate Brand Logo Upload (isolated)
 *
 * Uploads a manager's brand logo to S3 under the `manager-brands/` folder and
 * returns the bare S3 KEY (e.g. "manager-brands/gold42/1700000000_logo.png").
 * The key — not a full URL — is what `updateManagerConfigs` persists as
 * `configs.BrandLogo`; `affiliateDetails` later hands back a presigned URL for
 * display. Kept OUT of the global upload manager so a logo never appears in the
 * app's "uploading…" video progress list.
 *
 * Flow mirrors uploadCustomBanner:
 *   1. POST /api/upload/sessions/            (createUploadSession with custom s3_key)
 *   2. PUT  to presigned S3 URL(s)           (uploadPartWithPresignedUrl)
 *   3. POST /api/upload/sessions/{id}/complete/
 *   4. return the s3_key                     (caller sends it as configs.BrandLogo)
 */

import {
  createUploadSession,
  completeUploadSession,
  uploadPartWithPresignedUrl,
} from '../Api/uploaderApiFunctions';

const BRAND_FOLDER = 'manager-brands';

/**
 * Build the S3 key: manager-brands/{folder}/{timestamp}_{first5}{ext}.
 * `folder` scopes logos per manager (affiliate code or id); falls back to
 * "brand" when unknown. Timestamp keeps keys unique so a re-upload isn't served
 * from a stale CDN cache.
 */
function generateBrandS3Key(filename, folder) {
  const timestamp = Date.now();
  const dotIndex = filename.lastIndexOf('.');
  const ext = dotIndex >= 0 ? filename.slice(dotIndex) : '';
  const nameNoExt = dotIndex >= 0 ? filename.slice(0, dotIndex) : filename;
  const cleanName = nameNoExt.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5) || 'logo';
  const cleanExt = ext.replace(/[^a-zA-Z0-9.]/g, '');
  const cleanFolder = String(folder || 'brand').replace(/[^a-zA-Z0-9_-]/g, '') || 'brand';
  return `${BRAND_FOLDER}/${cleanFolder}/${timestamp}_${cleanName}${cleanExt}`;
}

/**
 * Upload a brand logo image and return its S3 key.
 *
 * @param {File} file - image file picked by the user
 * @param {string} [folder] - per-manager folder scope (e.g. affiliate code)
 * @param {Function} [onProgress] - optional (percent:number) => void
 * @returns {Promise<string>} S3 key of the uploaded logo
 */
export async function uploadBrandLogo(file, folder, onProgress) {
  const s3Key = generateBrandS3Key(file.name, folder);

  const session = await createUploadSession({
    file_name: file.name,
    file_size: file.size,
    content_type: file.type || 'image/png',
    s3_key: s3Key,
  });

  const presignedUrls = session.presigned_urls || [];
  const partSize = session.part_size || file.size;
  const totalParts = session.total_parts || 1;

  const parts = [];
  for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    const presignedUrlObj = presignedUrls.find(
      (u) => u.part_number === partNumber || u.partNumber === partNumber,
    );
    if (!presignedUrlObj) {
      throw new Error(`Missing presigned URL for part ${partNumber}`);
    }
    const presignedUrl = presignedUrlObj.presigned_url || presignedUrlObj.url;

    const part = await uploadPartWithPresignedUrl(
      presignedUrl,
      blob,
      partNumber,
      (loaded, total) => {
        if (onProgress && total) {
          const overall = ((start + loaded) / file.size) * 100;
          onProgress(Math.min(99, Math.round(overall)));
        }
      },
    );
    parts.push(part);
  }

  const sortedParts = parts.sort((a, b) => a.PartNumber - b.PartNumber);
  await completeUploadSession(session.id, sortedParts);

  if (onProgress) onProgress(100);

  return s3Key;
}

export default uploadBrandLogo;
